import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { TextAa, CheckCircle, XCircle, Camera } from "@phosphor-icons/react";

const LABELS = {
    surface_pct: "Superficie",
    price_sqm_pct: "€/m²",
    perizia_text_pct: "Testo perizia",
    deep_ready_pct: "Deep ready",
    ocr_text_pct: "Testo da OCR",
    surface_from_ocr_pct: "Superficie da OCR",
};

function Stat({ label, value, testid, accent }) {
    return (
        <div className="border border-gray-200 bg-white p-3" data-testid={testid}>
            <div className={`font-display-black text-2xl tabular-nums ${accent || ""}`}>{value}</div>
            <div className="overline mt-1">{label}</div>
        </div>
    );
}

export function OcrPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [capturing, setCapturing] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/ocr/status");
            setData(res.data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const captureBaseline = async () => {
        setCapturing(true);
        try {
            await api.post("/admin/ocr/baseline", null, { params: { force: true } });
            toast.success("Baseline qualità catturata (confronto prima/dopo azzerato a ora)");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setCapturing(false);
        }
    };

    if (loading) {
        return <div className="border border-gray-200 bg-white p-5 mb-8 text-sm text-gray-500" data-testid="ocr-panel-loading">Caricamento stato OCR…</div>;
    }
    if (!data) return null;

    return (
        <div className="mb-8" data-testid="ocr-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <TextAa size={16} weight="bold" />
                    <h2 className="font-display-black text-lg">OCR perizie — Tesseract locale (offline, nessun costo AI)</h2>
                </div>
                <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${data.ocr_available ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-rose-50 text-rose-700 border-rose-300"}`}
                    data-testid="ocr-availability"
                >
                    {data.ocr_available ? <CheckCircle size={12} weight="bold" /> : <XCircle size={12} weight="bold" />}
                    {data.ocr_available ? "OCR disponibile" : "OCR non disponibile"}
                </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                <Stat label="Documenti OCR" value={data.ocr_runs} testid="ocr-runs" />
                <Stat label="Pagine OCR" value={data.ocr_pages_total} testid="ocr-pages" />
                <Stat label="Caratteri estratti" value={(data.ocr_chars_total || 0).toLocaleString("it-IT")} testid="ocr-chars" />
                <Stat label="Superfici da OCR" value={data.ocr_surface_hits} testid="ocr-surface-hits" accent="text-indigo-600" />
                <Stat label="Errori OCR" value={data.ocr_errors} testid="ocr-errors" accent={data.ocr_errors ? "text-rose-600" : ""} />
            </div>

            {/* Before / after */}
            <div className="border border-gray-200 bg-white p-5" data-testid="ocr-before-after">
                <div className="flex items-center justify-between mb-4">
                    <div className="overline">Confronto qualità dati — prima vs dopo OCR</div>
                    <button
                        onClick={captureBaseline}
                        disabled={capturing}
                        data-testid="ocr-capture-baseline-btn"
                        className="border border-gray-300 hover:border-black h-8 px-3 text-xs flex items-center gap-2 transition-colors disabled:opacity-50"
                    >
                        <Camera size={13} weight="bold" /> {capturing ? "Cattura…" : "Cattura baseline"}
                    </button>
                </div>
                <div className="text-xs text-gray-400 mb-3">
                    Baseline: {data.baseline_taken_at ? data.baseline_taken_at.slice(0, 16).replace("T", " ") : "non catturata"}
                </div>
                <div className="space-y-3">
                    {data.before_after.map((ba) => {
                        const before = ba.before ?? 0;
                        const after = ba.after ?? 0;
                        const delta = after - before;
                        return (
                            <div key={ba.key} className="flex items-center gap-3" data-testid={`ocr-ba-${ba.key}`}>
                                <div className="w-32 shrink-0 text-xs text-gray-700">{LABELS[ba.key] || ba.key}</div>
                                <div className="flex-1 h-5 bg-gray-100 relative overflow-hidden">
                                    <div className="absolute inset-y-0 left-0 bg-gray-300" style={{ width: `${before}%` }} />
                                    <div className="absolute inset-y-0 left-0 bg-emerald-500/70" style={{ width: `${after}%` }} />
                                </div>
                                <div className="w-28 shrink-0 text-right text-xs tabular-nums">
                                    <span className="text-gray-400">{ba.before ?? "—"}%</span>
                                    <span className="mx-1 text-gray-300">→</span>
                                    <span className="font-bold">{ba.after ?? 0}%</span>
                                    {delta > 0 && <span className="ml-1 text-emerald-600 font-bold">+{delta}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
